import React, { useState, useMemo, useEffect } from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Sector, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useBudget } from '../../context/BudgetContext';
import { filterByPeriod, sumByType, accountBalance, groupByCategory, getMonthlyData, budgetVsActualSegments, getPlannedMonthlySavings, getPlannedVsActualSavingsByMonth, resolveBudgetAmount } from '../../utils/calculations';
import { ensureRatesForRange } from '../../utils/exchangeRates';
import { formatCurrency } from '../../utils/formatting';
import { CHART_COLORS, EXPENSE_PIE_SHADES, SAVINGS_PIE_SHADES, COLORBLIND_COLORS, MONTH_SHORT } from '../../utils/constants';
import { PeriodSelector, EmptyState } from '../Common/Common';
import { TrendingUp, TrendingDown, BarChart2, PiggyBank, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';

// Color triplets per metric: solid=achieved, light=under-target gap, dark=overshoot.
const TRI = {
  income:  { solid: '#2f8f6b', light: 'rgba(47,143,107,0.28)', dark: '#1d6b4f' },
  expense: { solid: '#cf6a5a', light: 'rgba(207,106,90,0.28)', dark: '#9e4636' },
  savings: { solid: '#3f7fae', light: 'rgba(63,127,174,0.28)', dark: '#2a5980' },
};

// Returns a pie slice color given the slice's value-rank (0 = biggest), total slices, chart type, and mode.
function getPieColor(rank, total, type, colorBlindMode) {
  if (colorBlindMode) return COLORBLIND_COLORS[rank % COLORBLIND_COLORS.length];
  const shades = type === 'expense' ? EXPENSE_PIE_SHADES : SAVINGS_PIE_SHADES;
  const idx = total <= 1 ? 0 : Math.round((rank / (total - 1)) * (shades.length - 1));
  return shades[idx];
}

const ActiveSliceShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx} cy={cy}
      innerRadius={innerRadius - 2}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      stroke="var(--bg-primary)"
      strokeWidth={2}
    />
  );
};

function MetricCard({ label, value, sub, className, trend }) {
  return (
    <div className={`metric-card ${className || ''}`}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {sub && <div className="metric-sub" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {trend === 'up' && <TrendingUp size={10} style={{ color: 'var(--income)' }} />}
        {trend === 'down' && <TrendingDown size={10} style={{ color: 'var(--expense)' }} />}
        {sub}
      </div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label, fmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

/* Chart legend swatch: a square in the slice color, with the element's emoji
   inside it when one is set. */
function ChartSwatch({ color, emoji }) {
  return (
    <span
      style={{
        width: 18, height: 18, borderRadius: 4, background: color, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, lineHeight: 1,
      }}
    >
      {emoji || null}
    </span>
  );
}

function Dashboard() {
  const { transactions, categories, budgetItems, buckets, yearlyExpenses, settings } = useBudget();
  const [period, setPeriod] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [dashYear, setDashYear] = useState(() => new Date().getFullYear());
  const [ratesTick, setRatesTick] = useState(0);
  const [activeExpenseIdx, setActiveExpenseIdx] = useState(null);
  const [activeSavingsIdx, setActiveSavingsIdx] = useState(null);
  const fmt = (v) => formatCurrency(v, settings);
  const mainCurrency = settings?.currency || 'USD';

  // Prefetch FX rates for any foreign buckets so the pie/bars are accurate.
  useEffect(() => {
    const foreign = [...new Set(buckets.map(b => b.currency).filter(c => c && c !== mainCurrency))];
    if (foreign.length === 0) return;
    const now = new Date();
    const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const start = new Date(now.getFullYear(), 0, 1);
    let cancelled = false;
    Promise.all(foreign.map(c => ensureRatesForRange(c, mainCurrency, ym(start), ym(now)).catch(() => {})))
      .then(() => { if (!cancelled) setRatesTick(t => t + 1); });
    return () => { cancelled = true; };
  }, [buckets, mainCurrency]);

  // Reset dashYear to current year when leaving 'year' period
  const handlePeriodChange = (p) => {
    if (p !== 'year') setDashYear(new Date().getFullYear());
    setPeriod(p);
  };

  const filtered = useMemo(
    () => filterByPeriod(transactions, period, customFrom, customTo, dashYear),
    [transactions, period, customFrom, customTo, dashYear]
  );

  const income     = useMemo(() => sumByType(filtered, 'income'),  [filtered]);
  const expenses   = useMemo(() => sumByType(filtered, 'expense'), [filtered]);
  const savingsTxn = useMemo(() => sumByType(filtered, 'savings'), [filtered]);
  const net = income - expenses - savingsTxn;

  // All-time available balance — independent of the period selector
  const availableBalance = useMemo(() => accountBalance(transactions), [transactions]);

  /* Period multiplier for budget scaling */
  const periodMonths = useMemo(() => {
    const now = new Date();
    if (period === 'prev' || period === 'month') return 1;
    if (period === 'ytd') return now.getMonth() + 1;
    if (period === 'year') return 12;
    if (period === 'custom' && customFrom && customTo) {
      const days = (new Date(customTo) - new Date(customFrom)) / 86400000;
      return Math.max(1, Math.round(days / 30));
    }
    return 1;
  }, [period, customFrom, customTo]);

  // Only count yearly expenses that are not archived for the viewed year
  const activeYearly = useMemo(
    () => yearlyExpenses.filter(y => !y.archiveYear || y.archiveYear > dashYear),
    [yearlyExpenses, dashYear]
  );

  // Current-month YYYY-MM key — used to resolve the "monthly budget" reference
  // for metric cards (history-aware, picks up overrides for the current month).
  const nowYM = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const budgetMonthlyIncome = useMemo(() =>
    categories
      .filter(c => c.type === 'income')
      .reduce((s, c) => s + resolveBudgetAmount(budgetItems.find(x => x.id === c.id), nowYM), 0),
    [categories, budgetItems, nowYM]
  );

  // Monthly expense budget = regular categories + yearly expenses amortised monthly (÷12)
  const budgetMonthlyExpense = useMemo(() =>
    categories
      .filter(c => c.type === 'expense')
      .reduce((s, c) => s + resolveBudgetAmount(budgetItems.find(x => x.id === c.id), nowYM), 0)
    + activeYearly.reduce((s, y) => s + (y.amount || 0) / 12, 0),
    [categories, budgetItems, activeYearly, nowYM]
  );

  const budgetPeriodExpense = budgetMonthlyExpense * periodMonths;

  const expenseByCategory = useMemo(() =>
    groupByCategory(
      filtered.filter(t => t.type === 'expense'),
      categories,
      yearlyExpenses
    ).map(e => {
      const b  = budgetItems.find(x => x.id === e.id);
      const ye = !b ? activeYearly.find(y => y.id === e.id) : null;
      const monthly = b ? resolveBudgetAmount(b, nowYM) : 0;
      const budget = b ? monthly * periodMonths : ye ? (ye.amount || 0) : 0;
      // showInfinity: ongoing yearly expense with no amount set (truly unbounded)
      const showInfinity = ye ? (!ye.toYear && !budget) : false;
      return { ...e, budget, showInfinity };
    }),
    [filtered, categories, yearlyExpenses, budgetItems, activeYearly, periodMonths, nowYM]
  );

  const monthlyData = useMemo(() => getMonthlyData(transactions, dashYear), [transactions, dashYear]);

  // Per-month budgets for the viewed dashYear — feeds the Budget vs Actual chart
  // so each month's bar reflects that month's planned amounts (history-aware).
  const monthlyBudgets = useMemo(() => MONTH_SHORT.map((_, i) => {
    const ym = `${dashYear}-${String(i + 1).padStart(2, '0')}`;
    const inc = categories
      .filter(c => c.type === 'income')
      .reduce((s, c) => s + resolveBudgetAmount(budgetItems.find(x => x.id === c.id), ym), 0);
    const exp = categories
      .filter(c => c.type === 'expense')
      .reduce((s, c) => s + resolveBudgetAmount(budgetItems.find(x => x.id === c.id), ym), 0)
      + activeYearly.reduce((s, y) => s + (y.amount || 0) / 12, 0);
    return { inc, exp };
  }), [categories, budgetItems, activeYearly, dashYear]);

  // Monthly savings target across all non-archived buckets, in main currency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthlySavingsTarget = useMemo(
    () => getPlannedMonthlySavings(buckets, mainCurrency),
    [buckets, mainCurrency, ratesTick]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const planVsActualData = useMemo(
    () => getPlannedVsActualSavingsByMonth(transactions, buckets, categories, budgetItems, dashYear, mainCurrency),
    [transactions, buckets, categories, budgetItems, dashYear, mainCurrency, ratesTick]
  );

  // Monthly budget vs actual — for each month produce achieved/gap/overshoot
  // segments per metric. Bar height = max(actual, target).
  const monthlyBudgetVsActual = useMemo(() => monthlyData.map((m, idx) => {
    const mb = monthlyBudgets[idx] || { inc: 0, exp: 0 };
    const i = budgetVsActualSegments(m.income,   mb.inc);
    const e = budgetVsActualSegments(m.expenses, mb.exp);
    const s = budgetVsActualSegments(m.savings,  monthlySavingsTarget);
    return {
      name: m.name,
      inc_done: i.done, inc_gap: i.gap, inc_over: i.over,
      exp_done: e.done, exp_gap: e.gap, exp_over: e.over,
      sav_done: s.done, sav_gap: s.gap, sav_over: s.over,
    };
  }), [monthlyData, monthlyBudgets, monthlySavingsTarget]);

  // Savings split by bucket for the active period.
  const savedByBucket = useMemo(() => {
    const map = new Map();
    filtered.filter(t => t.type === 'savings').forEach(t => {
      const id = t.bucket || '__none__';
      map.set(id, (map.get(id) || 0) + (Number(t.amount) || 0));
    });
    const list = [];
    map.forEach((value, id) => {
      if (value <= 0) return;
      const b = buckets.find(x => x.id === id);
      list.push({ id, name: b ? b.name : 'Unassigned', value });
    });
    return list.sort((a, b) => b.value - a.value);
  }, [filtered, buckets]);

  // Per-slice colors: rank by value so the biggest slice gets the darkest shade.
  const colorBlindMode = settings?.colorBlindMode || false;
  const expenseColors = useMemo(() => {
    const ranked = [...expenseByCategory].sort((a, b) => b.value - a.value);
    const rankOf = new Map(ranked.map((e, r) => [e.id, r]));
    return expenseByCategory.map(e => getPieColor(rankOf.get(e.id), ranked.length, 'expense', colorBlindMode));
  }, [expenseByCategory, colorBlindMode]);

  const savingsColors = useMemo(() => {
    // savedByBucket is already sorted desc, so index 0 is already the biggest.
    return savedByBucket.map((_, i) => getPieColor(i, savedByBucket.length, 'savings', colorBlindMode));
  }, [savedByBucket, colorBlindMode]);

  // Top categories ranked by absolute deviation from budget (period-scoped).
  const topCategoriesByDeviation = useMemo(() => expenseByCategory
    .filter(e => e.budget > 0)
    .map(e => ({ ...e, deviation: e.value - e.budget }))
    .sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation))
    .slice(0, 7),
    [expenseByCategory]);

  // Navigates to a specific month within dashYear as a custom period; clicking again deselects.
  function handleMonthClick(monthIdx) {
    if (selectedMonthIdx === monthIdx) {
      handlePeriodChange('month');
      return;
    }
    const y = dashYear;
    const from = `${y}-${String(monthIdx + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(y, monthIdx + 1, 0).getDate();
    const to = `${y}-${String(monthIdx + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setCustomFrom(from);
    setCustomTo(to);
    setPeriod('custom');
  }

  // Which month in dashYear is currently selected (for the bar chart highlight).
  const selectedMonthIdx = useMemo(() => {
    if (period !== 'custom' || !customFrom || !customTo) return -1;
    const from = new Date(customFrom + 'T00:00:00');
    const to   = new Date(customTo   + 'T00:00:00');
    if (from.getFullYear() !== dashYear || to.getFullYear() !== dashYear) return -1;
    if (from.getMonth() !== to.getMonth()) return -1;
    if (from.getDate() !== 1) return -1;
    if (to.getDate() !== new Date(dashYear, from.getMonth() + 1, 0).getDate()) return -1;
    return from.getMonth();
  }, [period, customFrom, customTo, dashYear]);

  // Human label for the active period — used in the Savings card sub-line.
  const periodLabel = useMemo(() => {
    if (period === 'month')  return 'this month';
    if (period === 'prev')   return 'last month';
    if (period === 'ytd')    return 'YTD';
    if (period === 'year')   return `in ${dashYear}`;
    if (period === 'custom') return 'in range';
    return '';
  }, [period, dashYear]);

  return (
    <div>
      <PeriodSelector
        period={period} onChange={handlePeriodChange}
        customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
      />

      {/* Year navigation — visible when "Full Year" period is selected */}
      {period === 'year' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setDashYear(y => y - 1)}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontWeight: 600, fontSize: 15, minWidth: 48, textAlign: 'center' }}>{dashYear}</span>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            onClick={() => setDashYear(y => y + 1)}
            disabled={dashYear >= new Date().getFullYear()}
          >
            <ChevronRight size={14} />
          </button>
          {dashYear !== new Date().getFullYear() && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setDashYear(new Date().getFullYear())}>
              Current year
            </button>
          )}
        </div>
      )}

      {/* All-time available balance (not affected by the period selector) +
          period savings-rate (web only, 1/3 width) on the same row */}
      <div className="balance-row">
        <div className={`card balance-hero${availableBalance < 0 ? ' negative' : ''}`}>
          <div className="metric-label">Current Account Available</div>
          <div className="metric-value" style={{ fontSize: 30 }}>
            {fmt(availableBalance)}
          </div>
          <div className="metric-sub">All-time · income − expenses − savings</div>
        </div>

        {(() => {
          const targetPct = settings?.savingsTargetPct ?? 20;
          const ratePct = income > 0 ? (savingsTxn / income) * 100 : 0;
          return (
            <div className="card savings-rate-card">
              <div className="card-header">
                <span className="card-title">Savings rate · {periodLabel}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--savings)' }}>target {targetPct}%</span>
              </div>
              <div className="metric-value" style={{ fontSize: 28, color: 'var(--savings)' }}>
                {income > 0 ? `${Math.round(ratePct)}%` : '—'}
              </div>
              <div className="progress-wrap" style={{ marginTop: 8 }}>
                <div
                  className="progress-fill"
                  style={{ width: `${Math.min(100, (ratePct / targetPct) * 100)}%`, background: 'var(--savings)' }}
                />
              </div>
              <div className="metric-sub">
                {income > 0
                  ? `${fmt(savingsTxn)} of ${fmt(income)} income saved ${periodLabel}`
                  : 'No income this period'}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Metrics */}
      <div className="metrics-grid">
        <MetricCard
          label="Total Income" className="metric-income"
          value={fmt(income)}
          sub={budgetMonthlyIncome > 0 ? `${Math.round((income / (budgetMonthlyIncome * periodMonths)) * 100)}% of target` : 'No budget set'}
        />
        <MetricCard
          label="Total Expenses" className="metric-expense"
          value={fmt(expenses)}
          sub={budgetPeriodExpense > 0
            ? (expenses > budgetPeriodExpense
                ? `${fmt(expenses - budgetPeriodExpense)} over budget`
                : `${Math.round((expenses / budgetPeriodExpense) * 100)}% of budget`)
            : 'No budget set'}
          trend={expenses > budgetPeriodExpense && budgetPeriodExpense > 0 ? 'down' : undefined}
        />
        <MetricCard
          label="Net Remaining" className="metric-savings"
          value={fmt(net)}
          sub={income > 0
            ? `${Math.round((savingsTxn / income) * 100)}% of income saved`
            : '—'}
          trend={net >= 0 ? 'up' : 'down'}
        />
        <MetricCard
          label="Savings"
          value={fmt(savingsTxn)}
          sub={buckets.length > 0
            ? `saved ${periodLabel} across ${buckets.length} bucket${buckets.length === 1 ? '' : 's'}`
            : 'No buckets yet'}
          trend={savingsTxn > 0 ? 'up' : undefined}
        />
      </div>

      {/* Charts row */}
      <div className="grid-2" style={{ marginBottom: 16 }}>
        {/* Expense breakdown with budget indicators */}
        <div className="card" onClick={() => setActiveExpenseIdx(null)}>
          <div className="card-header">
            <span className="card-title">Expense breakdown</span>
            {budgetPeriodExpense > 0 && (
              <span className={`mono ${expenses > budgetPeriodExpense ? 'expense-text' : 'income-text'}`} style={{ fontSize: 12 }}>
                {fmt(expenses)} / {fmt(budgetPeriodExpense)}
              </span>
            )}
          </div>
          {expenseByCategory.length === 0
            ? <EmptyState icon={<BarChart2 size={28} />} title="No expenses this period" sub="Start tracking to see the breakdown" />
            : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={expenseByCategory} cx="50%" cy="50%" innerRadius={48} outerRadius={76}
                      dataKey="value" paddingAngle={2}
                      activeIndex={activeExpenseIdx}
                      activeShape={ActiveSliceShape}
                      onClick={(_, index, e) => { e?.stopPropagation(); setActiveExpenseIdx(i => i === index ? null : index); }}
                      style={{ cursor: 'pointer' }}
                    >
                      {expenseByCategory.map((_, i) => (
                        <Cell
                          key={i}
                          fill={expenseColors[i]}
                          opacity={activeExpenseIdx === null || activeExpenseIdx === i ? 1 : 0.35}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Per-category budget bars */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {expenseByCategory.map((e, i) => {
                    const pct      = e.budget > 0 ? Math.round((e.value / e.budget) * 100) : null;
                    const over     = e.budget > 0 && e.value > e.budget;
                    const barColor = over ? 'var(--expense)' : pct !== null && pct >= 85 ? 'var(--warning)' : 'var(--income)';
                    const showBar  = e.budget > 0 || e.showInfinity;
                    const yearPct  = Math.round(((new Date().getMonth() + 1) / 12) * 100);
                    const isActive = activeExpenseIdx === i;
                    return (
                      <div
                        key={e.id}
                        onClick={(e) => { e.stopPropagation(); setActiveExpenseIdx(j => j === i ? null : i); }}
                        style={{
                          cursor: 'pointer', borderRadius: 6, padding: '2px 4px',
                          background: isActive ? 'var(--bg-secondary)' : 'transparent',
                          outline: isActive ? `1px solid ${expenseColors[i]}` : 'none',
                          opacity: activeExpenseIdx === null || isActive ? 1 : 0.5,
                          transition: 'opacity 0.15s, background 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <ChartSwatch color={expenseColors[i]} emoji={categories.find(c => c.id === e.id)?.emoji} />
                          <span style={{ flex: 1, fontSize: 14, color: 'var(--text-secondary)' }}>{e.name}</span>
                          <span className="mono" style={{ fontSize: 14, color: over ? 'var(--expense)' : 'var(--text-primary)' }}>
                            {fmt(e.value)}
                          </span>
                          {!e.showInfinity && e.budget > 0 && (
                            <span className="muted" style={{ fontSize: 11 }}>/ {fmt(e.budget)}</span>
                          )}
                          {!e.showInfinity && pct !== null && (
                            <span style={{ fontSize: 11, minWidth: 34, textAlign: 'right', color: over ? 'var(--expense)' : 'var(--text-tertiary)' }}>
                              {pct}%
                            </span>
                          )}
                        </div>
                        {showBar && (
                          <div style={{ height: 3, background: 'var(--bg-tertiary)', borderRadius: 100, overflow: 'hidden' }}>
                            <div style={{
                              width: `${e.showInfinity ? yearPct : Math.min(100, pct)}%`, height: '100%', borderRadius: 100,
                              background: barColor, transition: 'width 0.3s ease',
                            }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
        </div>

        {/* Savings by bucket — period-scoped pie */}
        <div className="card" onClick={() => setActiveSavingsIdx(null)}>
          <div className="card-header">
            <span className="card-title">Savings by bucket</span>
            <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmt(savingsTxn)}</span>
          </div>
          {savedByBucket.length === 0
            ? <EmptyState icon={<PiggyBank size={28} />} title={`No savings ${periodLabel}`} sub="Log a savings transaction to see the split" />
            : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={savedByBucket} cx="50%" cy="50%" innerRadius={48} outerRadius={86}
                      dataKey="value" paddingAngle={2}
                      activeIndex={activeSavingsIdx}
                      activeShape={ActiveSliceShape}
                      onClick={(_, index, e) => { e?.stopPropagation(); setActiveSavingsIdx(i => i === index ? null : index); }}
                      style={{ cursor: 'pointer' }}
                    >
                      {savedByBucket.map((_, i) => (
                        <Cell
                          key={i}
                          fill={savingsColors[i]}
                          opacity={activeSavingsIdx === null || activeSavingsIdx === i ? 1 : 0.35}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  {savedByBucket.map((s, i) => {
                    const pct = savingsTxn > 0 ? Math.round((s.value / savingsTxn) * 100) : 0;
                    const isActive = activeSavingsIdx === i;
                    return (
                      <div
                        key={s.id}
                        onClick={(e) => { e.stopPropagation(); setActiveSavingsIdx(j => j === i ? null : i); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          cursor: 'pointer', borderRadius: 6, padding: '2px 4px',
                          background: isActive ? 'var(--bg-secondary)' : 'transparent',
                          outline: isActive ? `1px solid ${savingsColors[i]}` : 'none',
                          opacity: activeSavingsIdx === null || isActive ? 1 : 0.5,
                          transition: 'opacity 0.15s, background 0.15s',
                        }}
                      >
                        <ChartSwatch color={savingsColors[i]} emoji={buckets.find(b => b.id === s.id)?.emoji} />
                        <span style={{ flex: 1, fontSize: 14, color: 'var(--text-secondary)' }}>{s.name}</span>
                        <span className="mono" style={{ fontSize: 14 }}>{fmt(s.value)}</span>
                        <span style={{ fontSize: 11, minWidth: 34, textAlign: 'right', color: 'var(--text-tertiary)' }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
        </div>
      </div>

      {/* Budget vs Actual — replaces the old Monthly overview */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Budget vs actual ({dashYear})</span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            solid = achieved · light = under target · dark = overshoot
          </span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={monthlyBudgetVsActual}
            margin={{ top: 8, right: 4, bottom: 0, left: -20 }}
            style={{ cursor: 'pointer' }}
            onClick={(data) => {
              if (data?.activeLabel) {
                const idx = MONTH_SHORT.indexOf(data.activeLabel);
                if (idx !== -1) handleMonthClick(idx);
              }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="name"
              tick={(p) => {
                const selected = p.payload.index === selectedMonthIdx;
                return (
                  <text
                    x={p.x} y={p.y + 10}
                    textAnchor="middle"
                    fill={selected ? 'var(--savings)' : 'var(--text-tertiary)'}
                    fontWeight={selected ? 700 : 400}
                    fontSize={10}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleMonthClick(p.payload.index)}
                  >
                    {p.payload.value}
                  </text>
                );
              }}
            />
            <YAxis tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
            <Tooltip content={<CustomTooltip fmt={fmt} />} />
            <Bar stackId="inc" dataKey="inc_done" name="Income"          fill={TRI.income.solid} />
            <Bar stackId="inc" dataKey="inc_gap"  name="Income under"    fill={TRI.income.light} />
            <Bar stackId="inc" dataKey="inc_over" name="Income over"     fill={TRI.income.dark} />
            <Bar stackId="exp" dataKey="exp_done" name="Expenses"        fill={TRI.expense.solid} />
            <Bar stackId="exp" dataKey="exp_gap"  name="Expenses under"  fill={TRI.expense.light} />
            <Bar stackId="exp" dataKey="exp_over" name="Expenses over"   fill={TRI.expense.dark} />
            <Bar stackId="sav" dataKey="sav_done" name="Savings"         fill={TRI.savings.solid} />
            <Bar stackId="sav" dataKey="sav_gap"  name="Savings under"   fill={TRI.savings.light} />
            <Bar stackId="sav" dataKey="sav_over" name="Savings over"    fill={TRI.savings.dark} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Planned vs actual savings rate */}
      {buckets.some(b => !b.archived && Number(b.monthlyTarget) > 0) && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <PiggyBank size={14} /> Savings rate · planned vs actual ({dashYear})
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>% of monthly income</span>
          </div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <BarChart
                data={planVsActualData}
                margin={{ top: 8, right: 16, bottom: 4, left: -12 }}
                style={{ cursor: 'pointer' }}
                onClick={(data) => {
                  if (data?.activeLabel) {
                    const idx = MONTH_SHORT.indexOf(data.activeLabel);
                    if (idx !== -1) handleMonthClick(idx);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="name"
                  tick={(p) => {
                    const selected = p.payload.index === selectedMonthIdx;
                    return (
                      <text
                        x={p.x} y={p.y + 10}
                        textAnchor="middle"
                        fill={selected ? 'var(--savings)' : 'var(--text-secondary)'}
                        fontWeight={selected ? 700 : 400}
                        fontSize={11}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleMonthClick(p.payload.index)}
                      >
                        {p.payload.value}
                      </text>
                    );
                  }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                  tickFormatter={v => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border)',
                    borderRadius: 8, fontSize: 12,
                  }}
                  formatter={(v, n, p) => {
                    if (n === 'Planned %') return [`${v}% (${fmt(p.payload.plannedAmt)})`, n];
                    if (n === 'Actual %')  return [`${v}% (${fmt(p.payload.actualAmt)})`, n];
                    return [v, n];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="plannedPct" name="Planned %" fill="rgba(59,130,246,0.4)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actualPct"  name="Actual %"  fill="var(--savings)"        radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top categories — over/under budget for the active period */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Top categories vs budget</span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{periodLabel}</span>
        </div>
        {topCategoriesByDeviation.length === 0
          ? <EmptyState icon={<AlertTriangle size={28} />} title="No budgeted spending yet" sub="Set a budget on a category to see deviations" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 4px 0' }}>
              {(() => {
                const maxAbs = Math.max(1, ...topCategoriesByDeviation.map(c => Math.abs(c.deviation)));
                return topCategoriesByDeviation.map(c => {
                  const over = c.deviation > 0;
                  const widthPct = (Math.abs(c.deviation) / maxAbs) * 50; // half-width axis on each side
                  return (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span style={{ flex: '0 0 28%', textAlign: 'right', color: 'var(--text-secondary)' }}>{c.name}</span>
                      <div style={{ flex: 1, display: 'flex', height: 14, position: 'relative', background: 'var(--bg-tertiary)', borderRadius: 4 }}>
                        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'var(--border)' }} />
                        {!over && (
                          <div style={{ position: 'absolute', right: '50%', top: 0, bottom: 0, width: `${widthPct}%`, background: 'var(--income)', borderRadius: '4px 0 0 4px' }} />
                        )}
                        {over && (
                          <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: `${widthPct}%`, background: 'var(--expense)', borderRadius: '0 4px 4px 0' }} />
                        )}
                      </div>
                      <span className="mono" style={{ flex: '0 0 22%', fontSize: 11, color: over ? 'var(--expense)' : 'var(--income)' }}>
                        {over ? '+' : '−'}{fmt(Math.abs(c.deviation))}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          )}
      </div>

    </div>
  );
}

export default React.memo(Dashboard);
